import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { ResponsibilityPicker } from './MyProfile';

const STATUS_CONFIG = {
  pending:           { label:'Pending',           cls:'badge-amber'  },
  approved:          { label:'Approved',          cls:'badge-green'  },
  rejected:          { label:'Rejected',          cls:'badge-red'    },
  changes_requested: { label:'Changes Requested', cls:'badge-indigo' },
};

function ReviewModal({ subId, onClose, onDone }) {
  const [sub, setSub]       = useState(null);
  const [status, setStatus] = useState('approved');
  const [notes, setNotes]   = useState('');
  const [resps, setResps]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    api.get(`/submissions/${subId}`).then(r => {
      const d = r.data.data;
      setSub(d);
      setResps(d.responsibilities?.map(r => r.responsibility) || ['']);
      setNotes(d.admin_notes || '');
      setStatus(d.status === 'pending' ? 'approved' : d.status);
    });
    api.get('/responsibility-templates').then(r => setTemplates(r.data.data));
  }, [subId]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/submissions/${subId}/review`, {
        status, admin_notes: notes,
        responsibilities: status === 'approved' ? resps.filter(r => r.trim()) : undefined,
      });
      onDone();
    } catch(e) { alert(e.response?.data?.message || 'Error'); }
    setSaving(false);
  };

  const handlePreview = async () => {
    if (!sub) return;
    setPreviewing(true);
    try {
      const res = await api.post('/letters/preview', {
        employeeData: {
          first_name: sub.employee_name?.split(' ')[0],
          last_name: sub.employee_name?.split(' ').slice(1).join(' '),
          gender: sub.gender,
          university: sub.university,
          faculty: sub.faculty,
          internship_start: sub.internship_start,
          internship_end: sub.internship_end,
        },
        signatoryName: sub.signatory_name || 'Mr. H. M. R. U. K. Bandara',
        signatoryTitle: sub.signatory_title || 'Chief Executive Officer (CEO)',
        requestedBy: sub.requested_by,
        startDate: sub.internship_start || null,
        endDate: sub.internship_end || null,
        timeType: sub.time_type || null,
        letterStyle: sub.letter_type === 'Internship' ? 'internship' : sub.letter_type === 'Associate' ? 'associate' : sub.letter_type === 'Appointment' ? 'appointment' : 'permanent',
        responsibilities: resps.filter(r => r.trim()),
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { alert('Preview failed'); }
    setPreviewing(false);
  };

  const generateLetter = async () => {
    try {
      const style = sub.letter_type === 'Internship' ? 'internship' : sub.letter_type === 'Associate' ? 'associate' : sub.letter_type === 'Appointment' ? 'appointment' : 'permanent';
      const res = await api.post('/letters/service', {
        employeeId:    sub.employee_id,
        signatoryName: sub.signatory_name,
        signatoryTitle: sub.signatory_title,
        requestedBy:   sub.requested_by,
        letterStyle:   style,
        timeType:      sub.time_type || null,
        startDate:     sub.internship_start || null,
        endDate:       sub.internship_end   || null,
        university:    sub.university       || null,
        faculty:       sub.faculty          || null,
        responsibilities: resps.filter(r => r.trim()),
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `${sub.letter_type}_Letter_${sub.emp_code}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to generate letter'); }
  };

  if (!sub) return (
    <div className="modal-overlay"><div className="modal"><div className="card-body" style={{padding:40,textAlign:'center'}}>Loading…</div></div></div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg" style={{maxWidth:740}}>
        <div className="modal-header">
          <span className="modal-title">Review — {sub.employee_name}</span>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-outline btn-sm" onClick={handlePreview} disabled={previewing}>
              👁 {previewing ? 'Loading…' : 'Preview Letter'}
            </button>
            <button className="btn btn-icon btn-outline" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-body" style={{maxHeight:'68vh',overflowY:'auto'}}>
          {/* Employee info */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:20,background:'var(--slate-50)',borderRadius:10,padding:14}}>
            {[['Employee',sub.employee_name],['ID',sub.emp_code],['Designation',sub.designation||'—'],['Dept',sub.department||'—'],['Email',sub.email],['NIC',sub.nic],['Letter Type',sub.letter_type],['Status',sub.emp_status]].map(([k,v])=>(
              <div key={k}><span style={{fontSize:11,fontWeight:600,color:'var(--slate-500)'}}>{k}: </span><span style={{fontSize:13,color:'var(--navy)'}}>{v||'—'}</span></div>
            ))}
          </div>

          {/* Submitted details */}
          <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:10}}>📋 Submitted Details</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
            {[['University',sub.university],['Faculty',sub.faculty],['Degree',sub.degree],['Project',sub.project_title],['From',sub.internship_start?new Date(sub.internship_start).toLocaleDateString('en-GB'):null],['To',sub.internship_end?new Date(sub.internship_end).toLocaleDateString('en-GB'):null],['Requested By',sub.requested_by],['Signatory',`${sub.signatory_name} · ${sub.signatory_title}`]].filter(([,v])=>v).map(([k,v])=>(
              <div key={k} style={{background:'#fff',border:'1px solid var(--slate-100)',borderRadius:8,padding:'10px 14px'}}>
                <div style={{fontSize:11,fontWeight:600,color:'var(--slate-500)'}}>{k}</div>
                <div style={{fontSize:13,color:'var(--navy)',marginTop:2}}>{v}</div>
              </div>
            ))}
          </div>

          {/* Responsibilities — with template picker */}
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:10}}>⚡ Responsibilities (edit before approving)</div>
            <ResponsibilityPicker
              responsibilities={resps}
              setResponsibilities={setResps}
              templates={templates}
              disabled={false}
            />
          </div>

          {/* Decision */}
          <div style={{background:'var(--slate-50)',borderRadius:10,padding:14}}>
            <div style={{fontSize:13,fontWeight:600,color:'var(--navy)',marginBottom:10}}>Admin Decision</div>
            <div style={{display:'flex',gap:10,marginBottom:12}}>
              {[['approved','✅ Approve'],['changes_requested','✏️ Request Changes'],['rejected','❌ Reject']].map(([k,l])=>(
                <button key={k} onClick={()=>setStatus(k)}
                  style={{flex:1,padding:'9px',border:`2px solid ${status===k?'var(--indigo)':'var(--slate-200)'}`,borderRadius:8,background:status===k?'var(--indigo-pale)':'#fff',color:status===k?'var(--indigo)':'var(--slate-600)',fontSize:12,fontWeight:status===k?700:400,cursor:'pointer',transition:'all .15s'}}>
                  {l}
                </button>
              ))}
            </div>
            <div className="form-group">
              <label>Notes to Employee {status!=='approved'&&'*'}</label>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                placeholder={status==='approved'?'Optional note…':'Explain what needs to change…'}
                style={{minHeight:70}} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          {sub.status === 'approved' && (
            <button className="btn btn-success" onClick={generateLetter}>
              🖨 Print Letter
            </button>
          )}
          <button className="btn btn-primary" onClick={submit} disabled={saving || (status!=='approved' && !notes)}>
            {saving ? 'Saving…' : 'Save Decision'}
          </button>
          {status === 'approved' && (
            <button className="btn btn-success" onClick={async () => { await submit(); setTimeout(generateLetter, 600); }} disabled={saving}>
              ✅ Approve & Print Letter
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Submissions() {
  const [subs, setSubs]     = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = () => api.get('/submissions').then(r => setSubs(r.data.data));
  useEffect(() => { load(); }, []);

  const filtered = filter === 'all' ? subs : subs.filter(s => s.status === filter);
  const counts   = subs.reduce((a,s) => ({ ...a, [s.status]:(a[s.status]||0)+1 }), {});

  return (
    <div>
      <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
        {[['all','All'],['pending','Pending'],['approved','Approved'],['changes_requested','Changes Req.'],['rejected','Rejected']].map(([k,l])=>(
          <button key={k} onClick={()=>setFilter(k)}
            style={{padding:'9px 16px',borderRadius:10,border:`2px solid ${filter===k?'var(--indigo)':'transparent'}`,background:filter===k?'var(--indigo-pale)':'var(--slate-50)',color:filter===k?'var(--indigo)':'var(--slate-500)',fontSize:13,fontWeight:filter===k?700:400,cursor:'pointer',transition:'all .15s'}}>
            {l} ({k==='all'?subs.length:counts[k]||0})
          </button>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Employee Submissions</span>
          {counts.pending > 0 && <span className="badge badge-amber">{counts.pending} pending</span>}
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Employee</th><th>Type</th><th>University</th><th>Submitted</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id}>
                  <td>
                    <div className="emp-name-cell">
                      <div className="emp-avatar">{s.employee_name?.split(' ').map(x=>x[0]).join('').slice(0,2).toUpperCase()}</div>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>{s.employee_name}</div>
                        <div style={{fontSize:11,color:'var(--slate-500)'}}>{s.emp_code} {s.emp_status==='Inactive'&&<span style={{color:'var(--amber)',fontWeight:600}}>· Guest</span>}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className="badge badge-indigo">{s.letter_type}</span></td>
                  <td style={{fontSize:13,color:'var(--slate-600)'}}>{s.university||'—'}</td>
                  <td style={{fontSize:12,color:'var(--slate-500)'}}>{new Date(s.submitted_at).toLocaleDateString('en-GB')}</td>
                  <td><span className={`badge ${STATUS_CONFIG[s.status]?.cls}`}>{STATUS_CONFIG[s.status]?.label}</span></td>
                  <td><button className="btn btn-outline btn-sm" onClick={()=>setSelected(s.id)}>Review</button></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6}><div className="empty-state"><p>No {filter==='all'?'':filter} submissions.</p></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <ReviewModal subId={selected} onClose={()=>setSelected(null)} onDone={()=>{setSelected(null);load();}} />}
    </div>
  );
}
